import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Users, MapPin, FileText, Pencil, ArrowLeft, ArrowRight, CircleCheckBig, Clock } from "lucide-react";
import { Input, Textarea } from "@heroui/input";
import { DatePicker } from "@heroui/date-picker";
import { useSession } from "@clerk/clerk-react";
import { parseDate, Time } from "@internationalized/date";
import { Button } from "@heroui/button";
import { addToast } from "@heroui/toast";
import { Listbox, ListboxItem } from "@heroui/listbox";
import { Avatar } from "@heroui/avatar";
import { getNextAvailable15MinTime, getNextAvailable15MinTimeBasedOnDuration,
  getCurrentDate, getReadableTimeDiff, timeIntervals, formatDateToYMD, 
  formatDate, getShortMonthName, formatHumanDate
} from "@/utils/dates";
import TimeSelectorPopover from "../date-time/TimeSelectorPopover";
import { createMeet as createMeetApi } from "@/api/meets";
import RowSteps from "../steps/create-meets-steps";
import InviteMembersComp from "../members/invite-members-comp";
import GooglePlacesSearchDropdown from "../maps/location-search";
import TimeWheelDrawer from "../date-time/TimeWheelDrawer";
import DatePickerLike from "../date-pickers";

const CreateMeetsComp = ({ _onResp }) => {
const { session } = useSession();
const navigate = useNavigate();
const pageSteps = [{title: "Details"}, {title: "Participants"}, {title: "Confirm"}];
const locationDataObj = { name: "", place_id: "", lat: "", lng: "" };
const current_date = getCurrentDate()
const [currentTabStep, setCurrentTabStep] = useState(1);
const [title, setTitle] = useState("");
const [description, setDescription] = useState("");
const [creating, setCreating] = useState(false);
const [locationData, setLocationData] = useState(locationDataObj);
const [selectedDate, setSelectedDate] = useState(current_date);
const [selectedStartTime, setSelectedStartTime] = useState(getNextAvailable15MinTime());
const [selectedEndTime, setSelectedEndTime] = useState(getNextAvailable15MinTimeBasedOnDuration(1));
const [meetingDuration, setMeetingDuration] = useState(
  getReadableTimeDiff(getNextAvailable15MinTime(), getNextAvailable15MinTimeBasedOnDuration(1))
);
const [selectedMembersIds, setSelectedMembersIds] = useState([]);
const [selectedMembers, setSelectedMembers] = useState([]);

useEffect(() => {
  setMeetingDuration(getReadableTimeDiff(selectedStartTime, selectedEndTime));
}, [selectedStartTime, selectedEndTime]);

const handleClose = () => _onResp?.("close", null);

async function onCreateClick() {
  const token = await session.getToken();
  const params = {
    title,
    description,
    meeting_date: selectedDate,
    start_time: selectedStartTime,
    end_time: selectedEndTime,
    members: selectedMembersIds,
    location: locationData
  };

  setCreating(true);
  createMeetApi(params, token).then((_resp) => {
      if (_resp?.data?.Result === "SUCCESS") {
        _onResp?.("success", _resp.data);
        addToast({ title: "Meets Created", color: "success" });
        navigate("/");
      }
    }).catch((err) => console.error(err))
    .finally(() => setCreating(false));
}

function onInviteMembersCompChange(_cmd, _value){
  if(_cmd === 'remove'){
    setSelectedMembersIds(prev => prev.filter(item => item !== _value['id']));
    setSelectedMembers(prev => prev.filter(item => item['id'] !== _value['id']));
  }else if(_cmd === 'add'){
    setSelectedMembersIds(prev => prev.includes(_value['id']) ? prev : [...prev, _value['id']]);
    setSelectedMembers(prev => prev.find(m => m.id === _value.id) ? prev : [...prev, _value]);
  }
}

function onNextClick() {
  if(currentTabStep === 1){
    if(title === ''){
      addToast({title: "Title Missing", color: 'primary'}); return;
    }
    if(JSON.stringify(locationData) === JSON.stringify(locationDataObj)){
      addToast({title: "Location Missing", color: 'primary'}); return;
    }
    // if(description === ''){
    //   addToast({title: "Description Missing", color: 'primary'}); return;
    // }
  }
  if(currentTabStep === 2 && selectedMembersIds.length === 0){
    addToast({title: "Select Participants", color: 'primary'}); return;
  }
  setCurrentTabStep(prev => prev + 1);
}

function onBackClick() {
  if(currentTabStep === 1){return}
  setCurrentTabStep(prev => prev - 1);
}

function onGooglePlacesSearchDropdownChange(_obj){
  setLocationData({
    name: _obj['name'],
    place_id: _obj['place_id'],
    lat: _obj['lat'],
    lng: _obj['lng'],
  });
}

return (
  <>
    <div className="flex flex-col gap-6 ps-4 pe-4 pt-2">
      <RowSteps currentStep={currentTabStep - 1} steps={pageSteps} />

      {currentTabStep === 1 && (
        <div className="flex flex-col gap-6">
          {/* Title */}
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
              <Pencil className="w-4 h-4" />
              <span className="text-xs font-medium tracking-wide">Title</span>
            </label>

            <Input
              onChange={(e) => setTitle(e.target.value)}
              value={title}
              variant="bordered"
              placeholder="Add title and time"
              classNames={{
                inputWrapper: `
                  bg-white border border-neutral-200 hover:bg-neutral-50 rounded-xl shadow-sm
                  dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
                  focus:outline-none focus:ring-0
                  data-[focus=true]:ring-0 data-[focus=true]:outline-none data-[focus=true]:border-neutral-300
                `,
                input: `
                  text-neutral-900 placeholder:text-neutral-400 text-base
                  dark:text-foreground focus:outline-none focus:ring-0
                `,
              }}
            />
          </div>

          {/* Date & Time */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
              <CalendarDays className="w-4 h-4" />
              <span className="text-xs font-medium tracking-wide">Event date</span>
            </div>

            <div className="flex flex-col gap-4">
              <DatePickerLike onChange={setSelectedDate} value={selectedDate} />

              {/* Time + Duration */}
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs font-medium tracking-wide">
                    Duration: {meetingDuration}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <TimeWheelDrawer
                    value={selectedStartTime}
                    onSelect={(v) => setSelectedStartTime(v)}
                  />
                  <span className="text-lg text-neutral-500 dark:text-neutral-400">–</span>
                  <TimeWheelDrawer
                    value={selectedEndTime}
                    onSelect={(v) => setSelectedEndTime(v)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
              <MapPin className="w-4 h-4" />
              <span className="text-xs font-medium tracking-wide">Location</span>
            </label>

            <GooglePlacesSearchDropdown
              onSelect={(place) => onGooglePlacesSearchDropdownChange(place)}
              placeholder="Type an address or place"
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                <FileText className="w-4 h-4" />
                <span className="text-xs font-medium tracking-wide">Description</span>
              </label>
              <small className="text-xs text-neutral-500 dark:text-neutral-400">Optional</small>
            </div>

            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description..."
              minRows={6}
              className="min-h-48"
              classNames={{
                inputWrapper: `
                  bg-white border border-neutral-200 hover:bg-neutral-50 rounded-xl shadow-sm
                  dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
                  focus:outline-none focus:ring-0
                  data-[focus=true]:ring-0 data-[focus=true]:outline-none data-[focus=true]:border-neutral-300
                  data-[focus=true]:after:ring-0 data-[focus=true]:after:shadow-none data-[focus=true]:after:outline-none
                `,
                input: `
                  text-neutral-800 placeholder:text-neutral-400 dark:text-foreground
                  resize-none appearance-none
                  outline-none focus:outline-none focus-visible:outline-none
                  ring-0 focus:ring-0 focus-visible:ring-0
                  [-webkit-tap-highlight-color:transparent]
                `,
              }}
            />
          </div>
        </div>
      )}

      {currentTabStep === 2 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300">
            <Users className="w-5 h-5" />
            <span className="text-sm">
              Participants {selectedMembersIds.length > 0 ? selectedMembersIds.length : null}
            </span>
          </div>
          <InviteMembersComp currentMembers={selectedMembersIds} onChange={onInviteMembersCompChange}/>
        </div>
      )}

      {currentTabStep === 3 && (
        <div className="pt-2">
          <h1 className="text-2xl font-bold leading-7 capitalize">{title}</h1>
          <p className="text-sm text-neutral-700 dark:text-zinc-100 first-letter:uppercase">
            {description}
          </p>

          <div className="mt-6 flex flex-col gap-3">
            {/* Date pill */}
            <div className="flex gap-3 items-start">
              <div className="flex-none border border-neutral-200 dark:border-white/10 rounded-md text-center w-11 h-11 overflow-hidden flex flex-col bg-white dark:bg-white/5 shadow-sm dark:shadow-none">
                <div className="text-tiny bg-neutral-100 dark:bg-white/10 py-0.5 text-neutral-600 dark:text-white/70">
                  {getShortMonthName(formatDate(selectedDate))}
                </div>
                <div className="flex items-center justify-center font-semibold text-sm flex-1 text-neutral-800 dark:text-white/80">
                  {selectedDate.substring(8, 10)}
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                <p className="text-medium text-foreground font-medium">
                  {formatHumanDate(formatDate(selectedDate))}
                </p>
                <p className="text-small text-neutral-600 dark:text-zinc-300">
                  {selectedStartTime} - {selectedEndTime}
                </p>
              </div>
            </div>

            {/* Location badge */}
            <div className="flex gap-3 items-center">
              <div className="flex items-center justify-center border border-neutral-200 dark:border-white/10 rounded-md w-11 h-11 bg-white dark:bg-white/5 shadow-sm dark:shadow-none">
                <MapPin className="text-neutral-600 dark:text-white/70" size={20} />
              </div>

              <div className="flex flex-col gap-0.5">
                <div className="group gap-x-0.5 text-medium text-foreground font-medium">
                  {locationData.name}
                </div>
                <p className="text-small text-neutral-600 dark:text-default-500">location</p>
              </div>
            </div>

            {/* Participants list */}
            <div className="flex gap-3 items-start">
              <div className="flex-none border border-neutral-200 dark:border-white/10 rounded-md text-center w-11 h-11 overflow-hidden flex items-center justify-center bg-white dark:bg-white/5 shadow-sm dark:shadow-none">
                <Users className="w-5 h-5 text-neutral-600 dark:text-white/70" />
              </div>
              <div className="flex flex-col gap-0.5">
                <Listbox
                  items={selectedMembers}
                  classNames={{ list: "max-h-[500px] overflow-auto" }}
                  label="Assigned to"
                  variant="flat"
                >
                  {(item) => (
                    <ListboxItem key={item.id} textValue={item.fullname} className="p-0 pb-2">
                      <div className="flex items-center gap-2">
                        <Avatar size="sm" src={`https://api.dicebear.com/9.x/thumbs/svg?seed=${item.fullname}`} />
                        <div className="flex flex-col">
                          <span className="text-small capitalize">{item.fullname}</span>
                          <span className="text-tiny text-neutral-600 dark:text-default-500">
                            {item.email_address}
                          </span>
                        </div>
                      </div>
                    </ListboxItem>
                  )}
                </Listbox>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Bottom actions */}
    <div className="fixed left-1/2 -translate-x-1/2 bottom-[100px] z-[50] w-full ps-4 pe-4 pointer-events-none">
      <div className="flex justify-between ps-4 pe-4 pointer-events-auto">
        <div>
          {currentTabStep !== 1 && (
            <Button
              onPress={onBackClick}
              size="md"
              radius="full"
              color="default"
              variant="bordered"
              className="border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50 shadow-sm dark:border-default-200/60 dark:bg-content1/70 dark:hover:bg-content2/60 dark:text-foreground"
            >
              <ArrowLeft /> Back
            </Button>
          )}
        </div>
        <div>
          {currentTabStep === 3 ? (
            <Button
              isLoading={creating}
              disabled={creating}
              onPress={onCreateClick}
              size="md"
              radius="full"
              color="primary"
              variant="solid"
              className="flex items-center gap-2"
            >
              {creating ? "Creating..." : <>Create <CircleCheckBig size={16} /></>}
            </Button>
          ) : (
            <Button
              onPress={onNextClick}
              size="md"
              radius="full"
              color="primary"
              variant="solid"
            >
              Next <ArrowRight />
            </Button>
          )}
        </div>
      </div>
    </div>
  </>
);
};

export default CreateMeetsComp;